export interface TimeSlot {
  value: string;
  label: string;
}
export type MinuteStep = 1 | 5 | 10 | 15 | 30;

const pad2 = (n: number): string => String(n).padStart(2, "0");

export const time = {
  /** Generate "HH:mm" slots over [startHour, endHour) at minuteStep increments. */
  generateSlots: (
    minuteStep: MinuteStep = 15,
    startHour = 0,
    endHour = 24,
  ): TimeSlot[] => {
    const slots: TimeSlot[] = [];
    for (let h = startHour; h < endHour; h++) {
      for (let m = 0; m < 60; m += minuteStep) {
        const value = `${pad2(h)}:${pad2(m)}`;
        slots.push({ value, label: time.formatLabel(value) });
      }
    }
    return slots;
  },

  /** "15:15" -> "3:15 PM" */
  formatLabel: (value: string): string => {
    const [hStr, mStr] = value.split(":");
    const h = Number(hStr);
    const m = Number(mStr);
    if (Number.isNaN(h) || Number.isNaN(m)) return value;
    const ampm = h >= 12 ? "PM" : "AM";
    const display = h % 12 === 0 ? 12 : h % 12;
    return `${String(display)}:${pad2(m)} ${ampm}`;
  },

  /**
   * Lenient typeahead match. Two tracks:
   *
   * 1. Query contains an a/p/am/pm token — structured parse honoring half-of-day.
   *    "3p" matches 3:xx PM, "3:15pm" exact, "3:15a" matches 3:15 AM.
   *
   * 2. Pure-digit query — substring match against the slot's 12-hour compact digit
   *    form only (military time is intentionally ignored). "315" matches 3:15 AM/PM.
   *    "115" matches 1:15 AM/PM AND 11:15 AM/PM (substring of compact "1115").
   *    Colons in the query are stripped.
   */
  matchesQuery: (slot: TimeSlot, query: string): boolean => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return true;

    const [hhStr, mmStr] = slot.value.split(":");
    const hh = Number(hhStr);
    const mm = Number(mmStr);

    const hasLetters = /[ap]/.test(trimmed);
    if (hasLetters) {
      const structured =
        /^(\d{1,2})(?::?(\d{1,2}))?\s*(a|p|am|pm)$/.exec(trimmed);
      if (!structured) return false;
      const qHours = Number(structured[1]);
      let qMinutes: number | undefined;
      if (structured[2]) {
        qMinutes =
          structured[2].length === 1
            ? Number(structured[2]) * 10
            : Number(structured[2]);
      }
      const ampm = structured[3].startsWith("a") ? "am" : "pm";
      let target = qHours;
      if (ampm === "pm" && target < 12) target += 12;
      if (ampm === "am" && target === 12) target = 0;
      if (hh !== target) return false;
      if (qMinutes !== undefined && mm !== qMinutes) return false;
      return true;
    }

    const digits = trimmed.replace(/[^0-9]/g, "");
    if (!digits) return false;

    const display12 = hh % 12 === 0 ? 12 : hh % 12;
    const compact12 = `${String(display12)}${pad2(mm)}`;
    const compact12Padded = `${pad2(display12)}${pad2(mm)}`;

    return compact12.includes(digits) || compact12Padded.includes(digits);
  },

  /** Current time rounded up to next minute step, as "HH:mm". */
  nowRounded: (minuteStep: MinuteStep = 15): string => {
    const d = new Date();
    let m = Math.ceil(d.getMinutes() / minuteStep) * minuteStep;
    let h = d.getHours();
    if (m >= 60) {
      m = 0;
      h = (h + 1) % 24;
    }
    return `${pad2(h)}:${pad2(m)}`;
  },

  /** Today's date in the local timezone as "YYYY-MM-DD". */
  todayDate: (): string => {
    const d = new Date();
    return `${String(d.getFullYear())}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  },

  /** Format "YYYY-MM-DD" as "Mar 21, 2026". */
  formatDateLabel: (dateString: string): string => {
    const [y, m, d] = dateString.split("-").map(Number);
    if (!y || !m || !d) return dateString;
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  },

  /** Split "YYYY-MM-DDTHH:mm" into parts. Empty strings if unset. */
  splitDateTime: (value: string): { date: string; time: string } => {
    if (!value) return { date: "", time: "" };
    const [date = "", timePart = ""] = value.split("T");
    const time = timePart.slice(0, 5);
    return { date, time };
  },

  /** Combine "YYYY-MM-DD" + "HH:mm" into "YYYY-MM-DDTHH:mm". */
  joinDateTime: (date: string, time: string): string => {
    if (!date && !time) return "";
    return `${date}T${time}`;
  },
};
